<script setup>
import { ref } from 'vue'
import { TriangleAlert } from '@lucide/vue'
import { Badge, Button } from '@drama/ui'

import { finishNovelRelease } from '../../../api/novel/packs.js'
import AuditId from '../audit/AuditId.vue'
import { shortId } from '../audit/audit-text.js'
import { formatBytes, recordVariant, useReleaseText } from './release-text.js'
import '../audit/audit.css'

// 发布记录与日志：失败原因与日志按码翻译、原文放 title（R-6）。卡住的记录（进程在发布中重启留下、超过传输超时没推进的
// 进行中记录）给出提示，管理员可把它收尾成失败，放开这部作品的下一次发布（R-4）。
const props = defineProps({
  packId: { type: String, required: true },
  records: { type: Array, required: true },
  isAdmin: { type: Boolean, required: true },
})
// finished：收尾成功，父级重取发布区状态。
const emit = defineEmits(['finished'])

const { tr, apiErrorLine, dateTime, reasonText, logText } = useReleaseText()
const finishing = ref('')
const finishError = ref('')

async function finish(record) {
  finishing.value = record.release_id
  finishError.value = ''
  try {
    await finishNovelRelease(props.packId, record.release_id)
    emit('finished')
  } catch (error) {
    finishError.value = apiErrorLine(error)
  } finally {
    finishing.value = ''
  }
}

/** 操作人：邮箱优先，其次显示名；账号已不存在时只剩 id，显示短 id。完整身份放 title。 */
function operatorName(operator) {
  if (operator === null) return '—'
  return operator.email || operator.display_name || shortId(operator.user_id)
}

function operatorTitle(operator) {
  if (operator === null) return ''
  return [operator.display_name, operator.email, operator.user_id].filter(Boolean).join(' · ')
}

function failure(record) {
  return reasonText({ code: record.error_code, message: record.error_message })
}
</script>

<template>
  <div class="prr">
    <p class="prr__title">{{ tr('historyTitle') }} <Badge size="sm">{{ records.length }}</Badge></p>
    <p v-if="records.length === 0" class="prr__muted">{{ tr('historyEmpty') }}</p>
    <p v-if="finishError" class="prr__error" role="alert"><TriangleAlert :size="16" aria-hidden="true" /> {{ finishError }}</p>
    <article v-for="record in records" :key="record.release_id" class="prr__record" data-testid="novel-release-record">
      <div class="prr__head">
        <strong class="prr__mono">{{ record.version }}</strong>
        <span class="prr__id">{{ tr('releaseId') }} <AuditId :id="record.release_id" /></span>
        <Badge size="sm" :variant="recordVariant(record.status)">{{ tr(`status.${record.status}`) }}</Badge>
      </div>
      <dl class="prr__meta">
        <div><dt>{{ tr('operator') }}</dt><dd :title="operatorTitle(record.requested_by)">{{ operatorName(record.requested_by) }}</dd></div>
        <div><dt>{{ tr('startedAt') }}</dt><dd>{{ dateTime(record.created_at) }}</dd></div>
        <div><dt>{{ tr('transfer') }}</dt><dd>{{ record.stats.assets_copied }} / {{ record.stats.assets_reused }} · {{ formatBytes(record.stats.bytes_copied) }}</dd></div>
      </dl>
      <p v-if="record.error_code" class="prr__error" :title="failure(record).title">{{ failure(record).text }}</p>
      <div v-if="record.stale" class="prr__stale" role="status" data-testid="novel-release-stale">
        <p><TriangleAlert :size="16" aria-hidden="true" /> {{ tr(isAdmin ? 'stale.hint' : 'stale.hintOperator') }}</p>
        <Button
          v-if="isAdmin"
          variant="secondary"
          size="sm"
          :loading="finishing === record.release_id"
          :disabled="finishing !== ''"
          data-testid="novel-release-finish"
          @click="finish(record)"
        >{{ tr('stale.finish') }}</Button>
      </div>
      <ol class="prr__logs">
        <li v-for="(log, index) in record.logs" :key="index">
          <Badge size="sm" :variant="recordVariant(log.stage)">{{ tr(`status.${log.stage}`) }}</Badge>
          <span :title="logText(log).title || undefined">{{ logText(log).text }}</span>
          <small class="prr__muted">{{ dateTime(log.created_at) }}</small>
        </li>
      </ol>
    </article>
  </div>
</template>

<style scoped>
.prr { display: grid; gap: var(--drama-space-sm); }
.prr__title { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-xs); margin: 0; font-weight: var(--drama-font-weight-medium); }
.prr__record { display: grid; gap: var(--drama-space-sm); padding: var(--drama-space-sm) var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); }
.prr__head { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-sm); }
.prr__id { display: inline-flex; align-items: center; gap: var(--drama-space-xs); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.prr__meta { display: grid; gap: var(--drama-space-xs); margin: 0; }
.prr__meta div { display: flex; justify-content: space-between; gap: var(--drama-space-md); min-width: 0; }
.prr__meta dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.prr__meta dd { margin: 0; overflow-wrap: anywhere; text-align: right; }
.prr__stale { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--drama-space-sm); padding: var(--drama-space-sm); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); color: var(--drama-color-warning); }
.prr__stale p { display: flex; gap: var(--drama-space-xs); margin: 0; }
.prr__logs { display: grid; gap: var(--drama-space-xs); margin: 0; padding: 0; list-style: none; }
.prr__logs li { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-sm); }
.prr__muted { color: var(--drama-color-text-muted); }
.prr__error { display: flex; gap: var(--drama-space-xs); margin: 0; color: var(--drama-color-danger); overflow-wrap: anywhere; }
.prr__mono { font-family: var(--drama-font-family-mono, monospace); overflow-wrap: anywhere; }
</style>
